import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Wallet, Languages, BookOpen, Plane, Users, Award, MapPin, Building, Home as HomeIcon } from 'lucide-react';
import PageHero, { Section, InfoCard, CTABlock } from '../components/PageHero';
import { Button } from '../components/ui/button';

const why = [
  { icon: ShieldCheck, title: 'WHO / NMC Recognised', desc: 'Every partner university is listed in the WDOMS directory and approved by NMC India for FMGE / NExT eligibility.' },
  { icon: Wallet, title: 'Affordable Fees', desc: 'Complete 6-year MBBS in ₹18-30 lakhs — significantly cheaper than private colleges in India.' },
  { icon: Languages, title: '100% English Medium', desc: 'Government universities offer the entire MBBS program in English with qualified international faculty.' },
  { icon: BookOpen, title: 'No Donation Policy', desc: 'Direct admission based on NEET score. No capitation fee, no hidden middlemen markup.' },
  { icon: Plane, title: '3 Weeks Visa', desc: 'Streamlined student visa process with invitation letter, apostille, and travel briefing.' },
  { icon: Users, title: 'Strong Indian Community', desc: 'Every university has an active Indian student network for peer support and mentoring.' },
];

const steps = [
  { title: 'Choose the right university', desc: 'Free counselling to shortlist universities matching your NEET score, budget & regional preference.' },
  { title: 'Document apostille', desc: 'MEA apostille of 10th, 12th, birth certificate, passport & NEET scorecard.' },
  { title: 'University application', desc: 'We submit your application, invoice, and admission fee to the university.' },
  { title: 'Invitation letter', desc: 'Official invitation letter issued by Ministry of Foreign Affairs of Russia within 2–4 weeks.' },
  { title: 'Student visa', desc: 'Russian Embassy student visa granted in 10–15 days after biometrics.' },
  { title: 'Fly to Russia', desc: 'Airport pickup, hostel check-in, SIM, bank account & university orientation by our local team.' },
];

export default function MbbsInRussia() {
  return (
    <>
      <PageHero eyebrow="Complete Guide" title="MBBS in" highlight="Russia — A–Z Roadmap"
        subtitle="Everything Indian students & parents need to know about pursuing MBBS in Russia's top government medical universities — fees, eligibility, admission process, life & career pathway."
        breadcrumbs={[{ label: 'MBBS in Russia' }]} />

      <Section eyebrow="Why Russia" title="Why Study MBBS in" highlight="Russia?">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {why.map((w, i) => <InfoCard key={i} icon={w.icon} title={w.title}>{w.desc}</InfoCard>)}
        </div>
      </Section>

      <Section eyebrow="Admission Process" title="6-Step Path to" highlight="MBBS in Russia" muted>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {steps.map((s, i) => <InfoCard key={i} num={i + 1} title={s.title}>{s.desc}</InfoCard>)}
        </div>
      </Section>

      <Section eyebrow="Investment Overview" title="Cost of MBBS" highlight="in Russia">
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { title: 'Tuition', val: '$3,000–$7,000/yr', desc: 'Depending on university & location. Government-subsidised.' },
            { title: 'Hostel + Food', val: '$1,500–$2,000/yr', desc: 'On-campus twin-sharing hostel with Indian mess.' },
            { title: 'Total 6-Year', val: '₹18L–₹30L', desc: 'Complete inclusive cost — no hidden charges.' },
          ].map((c, i) => (
            <div key={i} className="bg-white rounded-2xl border border-border p-6 shadow-sm">
              <div className="text-xs uppercase tracking-widest text-brand font-bold">{c.title}</div>
              <div className="font-serif text-3xl font-bold text-primary mt-1">{c.val}</div>
              <p className="mt-2 text-sm text-muted-foreground">{c.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 text-center">
          <Link to="/universities">
            <Button className="bg-brand hover:bg-brand-dark text-white rounded-full h-11 px-6 font-semibold">Compare 64 University Fees</Button>
          </Link>
        </div>
      </Section>

      <CTABlock title="Not sure which university fits you?" subtitle="Free profile evaluation & personalised university shortlisting in 24 hours." />
    </>
  );
}
